import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Brain, ShieldCheck, Crosshair, Factory, Truck, Flask, CaretRight, CheckSquare, XSquare } from "@phosphor-icons/react";
import SystemDiagram from "../components/home/SystemDiagram";

const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.4, delay: i * 0.1 } }),
};

export default function HomePage() {
  return (
    <main>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/e8wsilmi_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%281%29.png"
            alt="DustSentinel-LX sensing node monitoring airlock ingress"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.25]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0b0d] via-[#0a0b0d]/90 to-[#0a0b0d]/60" />
        </div>
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <img
                src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/8oygxjbv_ChatGPT%20Image%20May%206%2C%202026%2C%2011_36_09%20AM%20%281%29.png"
                alt="DustSentinel-LX — Detect. Classify. Protect."
                className="h-16 md:h-20 w-auto object-contain mb-8"
              />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-[#f2f4f8] leading-[1.05] mb-6">
                Lunar Dust Ingress Monitoring for Habitats, Airlocks, and Pressurized Rovers
              </h1>
              <p className="text-base md:text-lg text-[#8c9eb3] leading-relaxed mb-10 max-w-lg">
                DustSentinel-LX detects, classifies, and helps contain respirable lunar dust events inside crewed systems.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none border border-transparent hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2"
                >
                  Request Technical Overview <ArrowRight size={16} />
                </Link>
                <Link
                  to="/product"
                  data-testid="hero-cta-secondary"
                  className="bg-transparent text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-6 py-3 rounded-none border border-[#2d3239] hover:bg-[#121519] transition-colors inline-flex items-center gap-2"
                >
                  View System Architecture <CaretRight size={16} />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <SystemDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section data-testid="why-it-matters-section" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        {/* Habitat interior image accent */}
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block">
          <img
            src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/kfjvtoua_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%282%29.png"
            alt="DustSentinel-LX installed in lunar habitat"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.2]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0b0d] via-[#0a0b0d]/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              The Problem
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              Habitable Lunar Systems Need Dust Ingress Control
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mt-12">
            {[
              {
                title: "Crew-Health Hazard",
                desc: "Lunar dust is sub-micron, abrasive, and chemically reactive. Respirable particles pose documented health risks to crew in enclosed habitable volumes.",
              },
              {
                title: "Standards-Defined",
                desc: "NASA-STD-3001 sets lunar dust contamination limits for habitable atmospheres. Technical briefs call for monitoring, alerting, and operational controls.",
              },
              {
                title: "Beyond Sensing",
                desc: "The challenge is not just detecting particles. It is understanding ingress events, correlating them with operational state, and driving containment response.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-8 hover:border-[#4b7399] transition-colors duration-300"
              >
                <h3 className="text-xl font-semibold text-[#f2f4f8] mb-3">{item.title}</h3>
                <p className="text-sm text-[#8c9eb3] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="font-mono text-xs text-[#8c9eb3] mt-8">
            Ref: NASA-STD-3001 Technical Brief — Lunar Dust (OCHMO-TB-011)
          </p>
        </div>
      </section>

      {/* DETECT / INTERPRET / RESPOND */}
      <section data-testid="capabilities-section" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              Subsystem Capabilities
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              What DustSentinel-LX Does
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {[
              {
                icon: <Eye size={28} weight="regular" />,
                label: "Detect",
                title: "Distributed Sensing at Ingress-Critical Zones",
                desc: "Multi-modal sensing nodes deployed at airlock thresholds, habitat breathing zones, and rover ingress corridors. Continuous particulate monitoring with sub-micron resolution.",
              },
              {
                icon: <Brain size={28} weight="regular" />,
                label: "Interpret",
                title: "Mission-State-Aware Event Inference",
                desc: "Context-driven classification of ingress events. The system correlates particle data with operational state — EVA return, airlock cycling, rover docking — to distinguish events from background.",
              },
              {
                icon: <ShieldCheck size={28} weight="regular" />,
                label: "Respond",
                title: "Bounded Containment Actions & Exposure Logging",
                desc: "Generates control requests to ECLSS and vehicle systems. Maintains an audit-grade cumulative exposure ledger for each habitable volume and crew member.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                data-testid={`capability-card-${item.label.toLowerCase()}`}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-8 hover:border-[#4b7399] transition-colors duration-300"
              >
                <div className="text-[#4b7399] mb-4">{item.icon}</div>
                <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#d97706] mb-2">{item.label}</p>
                <h3 className="text-lg font-semibold text-[#f2f4f8] mb-3">{item.title}</h3>
                <p className="text-sm text-[#8c9eb3] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATIONS */}
      <section data-testid="applications-section" className="relative py-20 md:py-32 border-b border-[#2d3239] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_dustsentinel-lx/artifacts/94k2zhp2_ChatGPT%20Image%20May%206%2C%202026%2C%2010_43_07%20AM%20%283%29.png"
            alt="DustSentinel-LX sensing node inside pressurized rover"
            className="absolute inset-0 w-full h-full object-cover opacity-[0.1]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0b0d]/90 via-[#0a0b0d]/85 to-[#0a0b0d]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              Deployment Environments
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              Designed for Lunar Habitable Systems
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <Factory size={24} weight="regular" />, title: "Fixed Habitat", desc: "Primary breathing zones, common areas, and crew quarters within permanent or semi-permanent lunar surface habitats." },
              { icon: <Crosshair size={24} weight="regular" />, title: "Airlock / Suitport", desc: "Threshold monitoring at the primary dust ingress vector. EVA return cycles represent the highest-risk contamination events." },
              { icon: <Truck size={24} weight="regular" />, title: "Pressurized Rover", desc: "Mobile habitable volume for extended traverses. NASA describes these as vehicles where crews live and work during long-duration surface operations." },
              { icon: <Flask size={24} weight="regular" />, title: "Sample Handling", desc: "Contamination-sensitive zones where regolith samples are transferred, processed, or stored within the pressurized envelope." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bg-[#121519] border border-[#2d3239] rounded-none p-6 hover:border-[#4b7399] transition-colors duration-300"
              >
                <div className="text-[#4b7399] mb-3">{item.icon}</div>
                <h3 className="text-base font-semibold text-[#f2f4f8] mb-2">{item.title}</h3>
                <p className="text-sm text-[#8c9eb3] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/applications" className="font-mono text-xs tracking-wider uppercase text-[#4b7399] hover:text-[#f2f4f8] transition-colors inline-flex items-center gap-2">
              View all deployment environments <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION */}
      <section data-testid="differentiation-section" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              Positioning
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-12">
              What We Are — and What We Are Not
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <p className="font-mono text-xs tracking-widest uppercase text-[#8c9eb3] mb-4">Not this</p>
              {[
                "External dust-removal hardware",
                "Generic air-quality sensing platform",
                "Broad environmental dashboard",
                "Consumer-grade IoT device",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-[#8c9eb3]">
                  <XSquare size={18} weight="regular" className="mt-0.5 text-[#2d3239] flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-4">This</p>
              {[
                "Ingress-event detection and classification",
                "Control-orchestration support for ECLSS",
                "Standards-linked exposure record keeping",
                "Mission-state-aware containment logic",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 text-[#f2f4f8]">
                  <CheckSquare size={18} weight="fill" className="mt-0.5 text-[#4b7399] flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TECHNICAL CREDIBILITY */}
      <section data-testid="credibility-section" className="py-20 md:py-32 border-b border-[#2d3239]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              Standards Alignment
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-8">
              Built Around Published Lunar Dust Requirements
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8 mt-8">
            {[
              { label: "Requirement-Linked", desc: "System concept aligned with NASA-STD-3001 habitable atmosphere contamination limits and monitoring guidance." },
              { label: "Operationally Grounded", desc: "Supports habitable-volume monitoring and crew alerting as described in NASA lunar dust technical briefs." },
              { label: "Architecture-Ready", desc: "Designed for integration with habitat ECLSS, airlock control systems, and mobile pressurized vehicle interfaces." },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="border-l-2 border-[#4b7399] pl-6 py-2"
              >
                <p className="font-mono text-xs tracking-widest uppercase text-[#4b7399] mb-2">{item.label}</p>
                <p className="text-sm text-[#8c9eb3] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 font-mono text-xs text-[#8c9eb3] space-y-1">
            <p>Ref: NASA-STD-3001 Vol 2 — Human Factors, Habitability, and Environmental Health</p>
            <p>Ref: OCHMO-TB-011 — Lunar Dust Technical Brief</p>
            <p>Ref: NASA Lunar Dust Mitigation Roadmap (2024)</p>
          </div>
        </div>
      </section>

      {/* PARTNER CTA */}
      <section data-testid="partner-cta-section" className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/10044730/pexels-photo-10044730.jpeg?auto=compress&cs=tinysrgb&w=1260"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
          />
          <div className="absolute inset-0 bg-[#0a0b0d]/90" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#4b7399] mb-4">
              Partner With Us
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#f2f4f8] mb-4 max-w-2xl mx-auto">
              For Habitat Integrators, Rover Developers, and Lunar Infrastructure Teams
            </h2>
            <p className="text-base text-[#8c9eb3] mb-10 max-w-xl mx-auto">
              Technical overview and architecture briefing available on request. We work with primes, integrators, and mission planners building habitable lunar systems.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="partner-cta-button"
                className="bg-[#4b7399] text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none hover:bg-[#3a5a78] transition-colors inline-flex items-center gap-2 justify-center"
              >
                Request Technical Overview <ArrowRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="bg-transparent text-[#f2f4f8] font-mono text-sm tracking-wider uppercase px-8 py-3 rounded-none border border-[#2d3239] hover:bg-[#121519] transition-colors inline-flex items-center gap-2 justify-center"
              >
                Architecture Briefing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
